import { useEffect, useRef } from 'react'

import { socketInstance } from 'lib/socket'

// Auction-lifecycle events emitted in rapid bursts (e.g. bulk launches or batch
// expiry). Only these events are debounced — everything else (offers, accepts, declines,
// sales...) is handled immediately.
export const BURST_SOCKET_EVENTS = [
  'vehicle_launch',
  'vehicle_launch_to_buy',
  'vehicle_expired',
  'upcoming_vehicle_live',
  'vehicle_move_to_parking',
  'vehicle_back_to_parkingloat',
  'vehicle_back_to_parkinglot',
  'vehicle_offer_expired',
]

// Subscribe `handler` to `events`. For burst events, the first event after a
// quiet period runs immediately (e.g. a single launch); later events in a
// burst are collapsed into one trailing call `delay` ms after the last one,
// but never later than `maxWait` ms after the first, so a long burst still
// refreshes periodically. Other events call `handler` immediately. Returns an
// unsubscribe function.
export const subscribeSocketEvents = (
  events,
  handler,
  { delay = 2000, maxWait = 5000 } = {},
) => {
  let timer = null
  let firstEventAt = 0
  let lastRunAt = 0

  const run = (...args) => {
    timer = null
    firstEventAt = 0
    lastRunAt = Date.now()
    handler(...args)
  }

  const debouncedHandler = (...args) => {
    // Quiet period (e.g. a single launch): refresh immediately.
    if (!timer && Date.now() - lastRunAt >= delay) return run(...args)

    // Inside a burst (bulk launch): batch into one trailing refresh,
    // at most `maxWait` ms after the first batched event.
    if (!firstEventAt) firstEventAt = Date.now()
    clearTimeout(timer)
    const wait = Math.max(
      0,
      Math.min(delay, firstEventAt + maxWait - Date.now()),
    )
    timer = setTimeout(() => run(...args), wait)
  }

  const listenerFor = (event) =>
    BURST_SOCKET_EVENTS.includes(event) ? debouncedHandler : handler

  events.forEach((event) => socketInstance.on(event, listenerFor(event)))

  return () => {
    clearTimeout(timer)
    events.forEach((event) => socketInstance.off(event, listenerFor(event)))
  }
}

// Subscribe to socket event(s): bulk-launch burst events are debounced (see
// above), all other events call the handler immediately.
export default function useDebouncedSocketListener(
  events,
  handler,
  delay = 2000,
) {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    const eventList = Array.isArray(events) ? events : [events]
    return subscribeSocketEvents(
      eventList,
      (...args) => handlerRef.current(...args),
      { delay },
    )
  }, [JSON.stringify(Array.isArray(events) ? events : [events]), delay])
}
